/**
 * 한글 문법 처리를 위한 유틸리티 함수들
 */

/**
 * 한글 문자열의 마지막 글자에 받침이 있는지 확인
 * @param text 검사할 텍스트
 * @returns 받침이 있으면 true, 없으면 false
 */
export function hasJongseong(text: string): boolean {
  if (!text || text.length === 0) return false;
  
  const lastChar = text.charCodeAt(text.length - 1);
  
  // 한글 범위 확인 (가 = 0xAC00, 힣 = 0xD7A3)
  if (lastChar < 0xAC00 || lastChar > 0xD7A3) {
    return false;
  }
  
  // 한글 유니코드 공식: ((초성 * 21) + 중성) * 28 + 종성 + 0xAC00
  // 종성이 0이면 받침이 없음
  const jongseong = (lastChar - 0xAC00) % 28;
  
  return jongseong !== 0;
}

/**
 * 한글 텍스트에 "로/으로" 조사를 적절히 붙임
 * @param text 텍스트
 * @returns 조사가 붙은 텍스트
 */
export function withRoEuro(text: string): string {
  if (!text) return text;
  
  const lastChar = text[text.length - 1];
  const lastCharCode = text.charCodeAt(text.length - 1);
  
  // 한글이 아닌 경우 기본적으로 "로"
  if (lastCharCode < 0xAC00 || lastCharCode > 0xD7A3) {
    return `${text}로`;
  }
  
  // 종성(받침) 확인
  const jongseong = (lastCharCode - 0xAC00) % 28;
  
  // 받침이 없거나 'ㄹ' 받침인 경우 "로"
  if (jongseong === 0 || jongseong === 8) {
    return `${text}로`;
  }
  
  // 그 외의 받침이 있는 경우 "으로"
  return `${text}으로`;
}

/**
 * 한글 텍스트에 "를/을" 조사를 적절히 붙임
 * @param text 텍스트
 * @returns 조사가 붙은 텍스트
 */
export function withReulEul(text: string): string {
  if (!text) return text;
  
  if (hasJongseong(text)) {
    return `${text}을`;
  }
  return `${text}를`;
}

/**
 * 한글 텍스트에 "가/이" 조사를 적절히 붙임
 * @param text 텍스트
 * @returns 조사가 붙은 텍스트
 */
export function withGaI(text: string): string {
  if (!text) return text;
  
  if (hasJongseong(text)) {
    return `${text}이`;
  }
  return `${text}가`;
}

/**
 * 한글 텍스트에 "와/과" 조사를 적절히 붙임
 * @param text 텍스트
 * @returns 조사가 붙은 텍스트
 */
export function withWaGwa(text: string): string {
  if (!text) return text;
  
  if (hasJongseong(text)) {
    return `${text}과`;
  }
  return `${text}와`;
}

/**
 * 한글 텍스트에 "는/은" 조사를 적절히 붙임
 * @param text 텍스트
 * @returns 조사가 붙은 텍스트
 */
export function withNeunEun(text: string): string {
  if (!text) return text;
  
  if (hasJongseong(text)) {
    return `${text}은`;
  }
  return `${text}는`;
}

/**
 * 한글 텍스트에 특정 조사를 적절히 붙임
 * @param text 텍스트
 * @param josaType 조사 타입
 * @returns 조사가 붙은 텍스트
 */
export function withJosa(text: string, josaType: '로/으로' | '를/을' | '가/이' | '와/과' | '는/은'): string {
  switch (josaType) {
    case '로/으로':
      return withRoEuro(text);
    case '를/을':
      return withReulEul(text);
    case '가/이':
      return withGaI(text);
    case '와/과':
      return withWaGwa(text);
    case '는/은':
      return withNeunEun(text);
    default:
      return text;
  }
}